"use client";

import { useMemo } from "react";
import { usePathname } from "next/navigation";
import CssBaseline from "@mui/material/CssBaseline";
import MuiThemeProvider from "@mui/material/styles/ThemeProvider";
import createTheme from "@mui/material/styles/createTheme";
import { AppRouterCacheProvider } from "@mui/material-nextjs/v15-appRouter";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import merge from "lodash/merge";
import stylisRTLPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import useSettings from "hooks/useSettings";
import customThemeOptions from "./theme-options";
export default function ThemeProvider({
  children
}) {
  const pathname = usePathname();
  const {
    settings
  } = useSettings();
  
  // Safely access direction with fallback to 'ltr' to prevent initialization errors
  const direction = settings?.direction || "ltr";
  
  const themeOptions = customThemeOptions(pathname);
  const mergedThemeOptions = merge({}, themeOptions, {
    direction: direction
  });
  const theme = createTheme(mergedThemeOptions);

  
// theme shadows
  theme.shadows[1] = "0px 1px 3px rgba(3, 0, 71, 0.09)";
  theme.shadows[2] = "0px 4px 16px rgba(43, 52, 69, 0.1)";
  theme.shadows[3] = "0px 8px 45px rgba(3, 0, 71, 0.09)";
  theme.shadows[4] = "0px 0px 28px rgba(3, 0, 71, 0.01)";
  theme.shadows[5] = "rgba(43, 52, 69, 0.05) 0px 0px 24px 0px,rgba(43, 52, 69, 0.05) 0px 3px 6px 0px";
  theme.shadows[6] = "#7d879c0d 0px 1px 3px 0px, #7d879c0d 0px 1px 2px 0px";
  theme.shadows[7] = "rgba(43, 52, 69, .07) 1px 1px 1px 0px";

  // Configure cache options ONCE to prevent hydration mismatch
  // The cache key must remain stable throughout the component's lifetime
  // to avoid Emotion cache reinitialization during React reconciliation
  const cacheOptions = useMemo(() => {
    // Use "ltr" as the default to match the initial server-side render
    // This prevents cache key changes when settings load from localStorage
    return {
      key: "muiltr",
      stylisPlugins: [prefixer]
    };
  }, []); // Empty dependency array ensures this never changes

  return <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppRouterCacheProvider options={cacheOptions}>
        <MuiThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </AppRouterCacheProvider>
    </LocalizationProvider>;
}